import type { Rule } from 'antd/es/form';
import { REGEX_USERNAME, ROLE_USER } from 'constants/common';
import { badgeOfUser, VerifiedBadge } from 'components/verified-tick';

export interface MemberFormValues {
  roles?: string;
  studioCreatorBranch?: 'DANCE' | 'NPC';
  department?: string | null;
  defaultShift?: string | null;
  username?: string;
  tiktokUid?: string;
  verifiedBadge: VerifiedBadge | 'NONE';
  nickName?: string;
  email?: string;
  phoneCode?: string;
  phoneNumber?: string;
  about?: string;
  isOutStanding: boolean;
  isVip: boolean;
  password?: string;
}

/** Công tắc chỉ hiện ở vai trò mà nó có tác dụng (docs/xac-minh-danh-tinh §5.1). */
export const showOutstanding = (role?: string) =>
  role === ROLE_USER.CREATOR || role === ROLE_USER.AGENCY;
export const showVip = (role?: string) => role === ROLE_USER.USER;

/** Giống server (admin-member-rules.hpCompanyFor) — chỉ để hiện nhãn "Nhân viên HP". */
export const hpCompanyFor = (role?: string, department?: string | null) =>
  role === ROLE_USER.STUDIO_CREATOR ? true : role === ROLE_USER.CREATOR ? false : !!department;

/**
 * Nhãn "Nhân viên HP" trong popup. Server chỉ tự tính lại khi vai trò / Bộ phận
 * đổi, nên khi chưa đổi phải hiện giá trị ĐANG LƯU (dữ liệu cũ có thể lệch quy tắc,
 * vd Mod không Bộ phận nhưng vẫn là nhân viên, vẫn chấm công / phiếu lương).
 */
export const hpStaffShown = (user: any | null, role?: string, department?: string | null) => {
  const untouched =
    !!user?.id &&
    (role ?? '') === (user?.roles ?? '') &&
    (department || null) === (user?.department || null);
  return untouched ? !!user?.isHpCompany : hpCompanyFor(role, department);
};

export const PHONE_CODES = ['84', '1', '44', '61', '65', '66', '81', '82', '86', '886'];

/**
 * Ô TikTok ID (username): server bắt buộc khi Thêm và @Matches(REGEX_USERNAME) khi Sửa,
 * nên để trống sẽ nhận lỗi tiếng Anh "username format wrong". `whitespace` để "   "
 * cũng báo bắt buộc; khoảng trắng đầu/cuối/giữa bị pattern chặn. Dùng với validateFirst.
 */
export const USERNAME_RULES: Rule[] = [
  { required: true, whitespace: true, message: 'Nhập TikTok ID' },
  { max: 50, message: 'Không được quá 50 kí tự' },
  { pattern: new RegExp(REGEX_USERNAME), message: 'TikTok ID không đúng định dạng' },
];

const digits = (v?: string | null) => String(v ?? '').replace(/\D/g, '');

export function initialValues(user: any | null): MemberFormValues {
  return {
    roles: user?.roles || undefined,
    studioCreatorBranch: user?.studioCreatorBranch || undefined,
    department: user?.department || undefined,
    defaultShift: user?.defaultShift || undefined,
    username: user?.username || '',
    tiktokUid: user?.tiktokUid || '',
    verifiedBadge: badgeOfUser(user) ?? 'NONE',
    nickName: user?.nickName || '',
    email: user?.email || '',
    phoneCode: digits(user?.phoneCode) || '84',
    phoneNumber: user?.phoneNumber || '',
    about: user?.about || '',
    isOutStanding: !!user?.isOutStanding,
    isVip: !!user?.isVip,
    password: '',
  };
}

const TEXT_FIELDS = ['username', 'nickName', 'email', 'phoneNumber', 'about'] as const;

/**
 * Sửa: chỉ gửi ô đã đổi, để không ghi đè thứ người khác vừa đổi (vd. người dùng
 * vừa xác minh email). Rỗng = xoá (không bỏ qua như formatData cũ).
 * Tạo: gửi đủ; VIP / Nổi bật chỉ bật được ở vai trò có tác dụng.
 */
export function buildPayload(
  values: MemberFormValues,
  initial: MemberFormValues,
  isEdit: boolean,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const changed = (k: keyof MemberFormValues) =>
    !isEdit || (values[k] ?? '') !== (initial[k] ?? '');

  if (changed('roles')) out.roles = values.roles;
  if (values.roles === ROLE_USER.STUDIO_CREATOR && changed('studioCreatorBranch')) {
    out.studioCreatorBranch = values.studioCreatorBranch;
  }
  if (changed('department')) out.department = values.department || null;
  if (changed('defaultShift')) out.defaultShift = values.defaultShift || null;
  for (const k of TEXT_FIELDS) if (changed(k)) out[k] = String(values[k] ?? '').trim();
  // Sửa: TikTok ID không xoá được (server từ chối ""); lỡ lọt qua rule thì bỏ, không gửi.
  if (isEdit && out.username === '') delete out.username;
  const uid = digits(values.tiktokUid);
  if (isEdit ? uid !== digits(initial.tiktokUid) : !!uid) out.tiktokUid = uid;
  if (changed('phoneCode')) out.phoneCode = digits(values.phoneCode) || null;
  if (changed('verifiedBadge')) {
    out.verifiedBadge = values.verifiedBadge === 'NONE' ? null : values.verifiedBadge;
  }
  if (!isEdit) {
    out.isOutStanding = showOutstanding(values.roles) && values.isOutStanding;
    out.isVip = showVip(values.roles) && values.isVip;
  } else {
    if (showOutstanding(values.roles) && changed('isOutStanding')) out.isOutStanding = values.isOutStanding;
    if (showVip(values.roles) && changed('isVip')) out.isVip = values.isVip;
  }
  if (values.password) out.password = values.password;
  return out;
}
